import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const images = [
  "/gallery/00.jpg",
  "/gallery/01.jpg",
  "/gallery/02.jpg",
  "/gallery/03.jpg",
  "/gallery/04.jpg",
  "/gallery/05.jpg",
  "/gallery/06.jpg",
  "/gallery/07.jpg",
  "/gallery/08.jpg",
  "/gallery/09.jpg",
  "/gallery/010.jpg",
  "/gallery/012.jpg",
  "/gallery/013.jpg",
  "/gallery/014.jpg",
  "/gallery/015.jpg",
  "/gallery/016.jpg",
  "/gallery/017.jpg",
  "/gallery/018.jpg",
  "/gallery/019.jpg",
  "/gallery/020.jpg",
  "/gallery/021.jpg",
  "/gallery/022.jpg",
  "/gallery/023.jpg",
  "/gallery/024.jpg",
  "/gallery/25.jpg",
  "/gallery/026.jpg",
  "/gallery/027.jpg",
  "/gallery/028.jpg",
  "/gallery/029.jpg",
  "/gallery/030.jpg",
  "/gallery/031.jpg",
  "/gallery/032.jpg",
  "/gallery/033.jpg",
  "/gallery/034.jpg",
  "/gallery/035.jpg",
  "/gallery/036.jpg",
  "/gallery/037.jpg",
  "/gallery/038.jpg",
  "/gallery/039.jpg",
  "/gallery/040.jpg",
];


const Gallery: React.FC<{ content?: any }> = ({ content }) => {
  const imagesList = content?.images || [
    "/gallery/00.jpg",
    "/gallery/01.jpg",
    "/gallery/02.jpg",
    "/gallery/03.jpg",
    "/gallery/04.jpg",
    // ... (fallback images)
  ];

  const [index, setIndex] = useState(0);

  const nextSlide = () => {
    setIndex((prev) => (prev + 1) % imagesList.length);
  };

  const prevSlide = () => {
    setIndex((prev) => (prev - 1 + imagesList.length) % imagesList.length);
  };

  // Get visible images (previous, current, next)
  const prevIndex = (index - 1 + imagesList.length) % imagesList.length;
  const nextIndex = (index + 1) % imagesList.length;

  return (
    <section className="w-full bg-white py-10">
      {/* Proper Title ABOVE slideshow */}
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900  mt-4">
          {content?.title || "Hamdan Transport Gallery"}
        </h2>
      </div>

      {/* Slideshow */}
      <div className="relative w-full h-[250px] sm:h-[350px] lg:h-[450px] flex items-center justify-center overflow-hidden px-4">
        <div className="flex items-center justify-center gap-2 sm:gap-4 lg:gap-6 relative w-full">
          {/* Previous Image */}
          <motion.img
            key={`prev-${imagesList[prevIndex]}`}
            src={imagesList[prevIndex]}
            alt="Previous"
            className="hidden sm:block w-1/4 h-[200px] sm:h-[250px] lg:h-[250px] object-cover opacity-50 scale-90 rounded-lg"
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 0.5, x: 0 }}
            transition={{ duration: 0.6 }}
          />

          {/* Current Image */}
          <motion.img
            key={`current-${imagesList[index]}`}
            src={imagesList[index]}
            alt="Current"
            className="w-full sm:w-3/4 lg:w-1/2 h-[250px] sm:h-[300px] lg:h-[400px] object-cover rounded-xl "
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          />

          {/* Next Image */}
          <motion.img
            key={`next-${imagesList[nextIndex]}`}
            src={imagesList[nextIndex]}
            alt="Next"
            className="hidden sm:block w-1/4 h-[200px] sm:h-[250px] lg:h-[250px] object-cover opacity-50 scale-90 rounded-lg"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 0.5, x: 0 }}
            transition={{ duration: 0.6 }}
          />
        </div>

        {/* Left Button */}
        <button
          onClick={prevSlide}
          className="absolute left-2 sm:left-4 lg:left-6 top-1/2 -translate-y-1/2 bg-black/50 p-2 sm:p-3 rounded-full text-white hover:bg-black/80 transition z-10"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" />
        </button>

        {/* Right Button */}
        <button
          onClick={nextSlide}
          className="absolute right-2 sm:right-4 lg:right-6 top-1/2 -translate-y-1/2 bg-black/50 p-2 sm:p-3 rounded-full text-white hover:bg-black/80 transition z-10"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" />
        </button>
      </div>

      {/* Mobile Image Counter */}
      <div className="flex justify-center mt-4 sm:hidden">
        <div className="bg-black/70 text-white px-3 py-1 rounded-full text-sm">
          {index + 1} / {imagesList.length}
        </div>
      </div>

      {/* Desktop Thumbnail Navigation */}
      <div className="hidden lg:flex justify-center mt-6 gap-2 px-6">
        <div className="flex gap-2 overflow-x-auto max-w-full">
          {imagesList.slice(Math.max(0, index - 2), Math.min(imagesList.length, index + 3)).map((img: any, i: number) => {
            const actualIndex = Math.max(0, index - 2) + i;
            return (
              <button
                key={actualIndex}
                onClick={() => setIndex(actualIndex)}
                className={`flex-shrink-0 w-16 h-12 rounded-md overflow-hidden border-2 transition ${actualIndex === index ? 'border-red-500' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
              >
                <img
                  src={img}
                  alt={`Thumbnail ${actualIndex + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Gallery;